import { Alert, Card, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import type { ReactNode } from 'react';
import { rawApi } from '../../api/client';
import { detailKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair } from '../../components/CashFigures';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatMoney } from '../../lib/format';

/**
 * The pouch that goes in the safe: what should be in it, what was, and the difference.
 *
 * > "When I review a nightly drop I should be able to see in the Admin site how much is supposed to
 * > be in that drop and either the money is all there or it isn't."
 *
 * The drawer is counted off, put back to its standard float in the right notes out of the float box,
 * and the profit goes into a bag. There was no screen anywhere that said what should be in the bag —
 * only what had been moved into the safe, with nothing to measure it against, so the sentence above
 * had no answer in the office at all.
 *
 * ## The line that stops this screen accusing somebody
 *
 * A guest leaves six dollars on a card slip and the bartender takes it out of the drawer, because
 * the card money is three days from the bank and the person who earned it is standing there tonight.
 * The drop is then six dollars lighter than the takings — **correctly** — and without a line saying
 * so it reads as six dollars missing, on the one shift with nobody else in the building. "Card tips
 * handed over" is that line, and the table underneath says to whom.
 *
 * ## Two things that are deliberately NOT here
 *
 * **Over and short.** It is on the cards above this panel and it answers a different question: did
 * the notes in the drawer match what the drawer should have held. This asks whether the bag matched
 * what the bag should have held. Running the two together would report one shortage twice.
 *
 * **Anything for a bartender to do.** Handing tips over and bagging a drop are gestures at the register.
 * This is the owner reading what happened, afterwards, at a time of his choosing.
 *
 * ## A NIGHTLY DROP IS NIGHTLY, AND THIS PANEL USED TO FORGET THAT
 *
 * His sentence is *"when I review a **nightly** drop"*. The drop is bagged when the drawer is
 * counted off; until that moment nothing is owed to the safe, and `dropped` is nought because the
 * shift has not finished, not because anybody is holding money back.
 *
 * The panel was rendered on an open drawer exactly as on a closed one, so a bar in the middle of a
 * Saturday read:
 *
 *   > Should go to the safe **L 8,000.00** · Went to the safe **L 0.00** · Difference
 *   > **L 8,000.00 still to drop** — *Either the money is all there or it isn't*
 *
 * Both halves of that are wrong in the same direction. "Still to drop" is a shortfall against a
 * deadline that has not arrived, on the one shift with nobody else in the building; and the same
 * arithmetic on a quiet drawer answers **All there** — a green verdict on a night that has not
 * happened, under the owner's own acceptance sentence.
 *
 * So the verdict is withheld while the drawer is trading, and the figure that IS still meaningful —
 * what is in the register above its float — says out loud that it moves with every sale. The card above
 * this panel already does exactly this with `basis: 'running' | 'atClose'`; this is the same
 * distinction, on the more consequential of the two figures.
 */

interface TipLine {
  staffName: string | null;
  currency: 'USD' | 'HNL';
  earnedOnCardsMinor: number;
  paidFromThisDrawerMinor: number;
  stillOwedMinor: number;
}

interface Drop {
  standardFloat: CashPair;
  expected: CashPair;
  cardTipsPaidInCash: CashPair;
  expectedDrop: CashPair;
  dropped: CashPair;
  dropVariance: CashPair;
  floatShortBy: CashPair;
  dropCount: number;
  tips: TipLine[];
}

function Figure({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Card withBorder>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {value}
      {hint && (
        <Text size="xs" c="dimmed" mt={2}>
          {hint}
        </Text>
      )}
    </Card>
  );
}

const isSomething = (pair: CashPair | undefined) =>
  Boolean(pair && (pair.usdMinor !== 0 || pair.hnlMinor !== 0));

/**
 * The difference between the bag and what the bag was owed, in words rather than as a signed number.
 *
 * "−6,400" is not a sentence, and the two directions do not mean the same thing: a bag that is light
 * is money to go looking for, while a bag heavier than the expected cash drop means the float was not put
 * back and tomorrow's shift opens short. Both need a name.
 *
 * ## AND A CURRENCY THE NIGHT NEVER TOUCHED IS NOT "ALL THERE"
 *
 * This is the same defect as the one corrected above it, one card to the right and one currency
 * over. The two currencies are kept apart everywhere in this domain — deliberately, because they are
 * two different piles of notes — so a night that took lempiras and no dollars answers nought on the
 * dollar side of every subtraction on this panel. Nought less nought is nought, and the card read
 * **All there · L 221.00 still to drop**: a verdict of clean beside a verdict of short, on the same
 * bag, one of them about money nobody handled.
 *
 * "All there" is his own acceptance sentence, and it means *the pouch held what it was supposed to
 * hold*. Where nothing was owed to the safe and nothing went, there is no pouch to be all there — so
 * the card says so and stays out of the way of the currency that has something to report. The test
 * is both figures rather than the difference: the difference alone cannot tell "nothing happened"
 * from "everything balanced".
 */
function DropDifference({
  minor,
  owed,
  dropped,
  currency,
}: {
  minor: number | undefined;
  owed: number | undefined;
  dropped: number | undefined;
  currency: 'USD' | 'HNL';
}) {
  if (minor === undefined) return <Text c="dimmed">—</Text>;
  if (owed === 0 && dropped === 0) {
    return (
      <Text size="sm" c="dimmed">
        No {currency === 'USD' ? 'dollars' : 'lempiras'} to drop
      </Text>
    );
  }
  if (minor === 0) return <Text size="sm">All there</Text>;
  const light = minor < 0;
  return (
    <Text size="sm" c={light ? 'red' : 'orange'} fw={600}>
      {formatMoney(Math.abs(minor), currency)} {light ? 'still to drop' : 'more than was owed'}
    </Text>
  );
}

/**
 * The one place this panel's cache key is written.
 *
 * Anything that moves what the drawer is expected to hold moves the drop with it — the owner's own
 * pull, above all, since adjusting the drawer changes the expectation this whole panel is derived
 * from. Exported so that the screen doing the moving invalidates the same entry this query reads
 * rather than a hand-written near-miss, which is the defect `api/queryKeys.ts` exists to prevent.
 */
export const dropKey = (drawerSessionId: string, locationId: string | undefined) =>
  detailKey('/admin/v1/cash/sessions/drop', `${drawerSessionId}|${locationId}`);

export function TheDrop({
  drawerSessionId,
  status,
}: {
  drawerSessionId: string;
  /** Trading, or counted off. The verdict below belongs only to the second. */
  status: 'open' | 'closed';
}) {
  const locationId = useLocationId();
  const trading = status === 'open';
  const drop = useQuery({
    queryKey: dropKey(drawerSessionId, locationId),
    queryFn: () =>
      rawApi<Drop>(
        'GET',
        `/admin/v1/cash/sessions/${drawerSessionId}/drop?locationId=${encodeURIComponent(locationId ?? '')}`,
      ),
    // The site is read once and shared; until it arrives there is no drawer to ask about.
    enabled: Boolean(drawerSessionId) && Boolean(locationId),
  });
  const record = drop.data;

  return (
    <Paper withBorder p="md" radius="md">
      <Title order={5}>The drop</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm">
        Cash to move to the safe after leaving the opening float in the drawer. This is a cash transfer, not
        profit. Putting the float back is a swap of notes for notes with the float box, so it changes nothing
        here — cash handed over to cover tips left on cards does, and it has its own line.
        {trading && ' This drawer is still trading, so the bag has not been made up yet.'}
      </Text>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Figure
          label={trading ? 'Above the float right now' : 'Should go to the safe'}
          value={<MoneyPair pair={record?.expectedDrop} fw={700} />}
          /*
           * "THE FLOAT THIS SHIFT OPENED ON", not "the float". This figure is read off the shift's
           * own opening count and no longer moves when the standard float is edited in the office —
           * it used to, and halving the float turned a shift counted off the day before from
           * L 221.00 owing into L 1,221.00 owing, which is an audit figure recomputed against
           * today's configuration. Naming WHICH float is what stops the question being asked of
           * this screen a third time.
           */
          hint={
            record
              ? `Everything above the float this shift opened on, ${formatMoney(record.standardFloat.usdMinor, 'USD')} · ${formatMoney(record.standardFloat.hnlMinor, 'HNL')}${
                  trading ? '. It moves with every sale — it is not what the bag owes yet' : ''
                }`
              : undefined
          }
        />
        <Figure
          label="Went to the safe"
          value={<MoneyPair pair={record?.dropped} fw={700} />}
          hint={
            record
              ? `${record.dropCount} bag${record.dropCount === 1 ? '' : 's'} counted in on this shift`
              : undefined
          }
        />
        {/*
          THE VERDICT, AND THE ONE STATE IT MUST NOT BE GIVEN IN.

          A drop is made up when the drawer is counted off. Before that, `dropped` is nought because
          the shift is unfinished — so this card would answer "all there" on a quiet drawer and
          "L 8,000.00 still to drop" on a busy one, and both are verdicts on a night nobody has
          finished trading. "Either the money is all there or it isn't" is the owner's own test of
          this screen and it deserves an answer only when there is something to be all there.
        */}
        <Figure
          label="Difference"
          value={
            trading ? (
              <Text size="sm" c="dimmed">
                Not yet
              </Text>
            ) : (
              <Group gap={10} wrap="nowrap">
                <DropDifference
                  minor={record?.dropVariance.usdMinor}
                  owed={record?.expectedDrop.usdMinor}
                  dropped={record?.dropped.usdMinor}
                  currency="USD"
                />
                <Text size="sm" c="dimmed">
                  ·
                </Text>
                <DropDifference
                  minor={record?.dropVariance.hnlMinor}
                  owed={record?.expectedDrop.hnlMinor}
                  dropped={record?.dropped.hnlMinor}
                  currency="HNL"
                />
              </Group>
            )
          }
          hint={
            trading
              ? 'The bag is made up when the drawer is counted off. Nothing is owed to the safe until then'
              : "Either the money is all there or it isn't"
          }
        />
        <Figure
          label="Card tips handed over"
          value={<MoneyPair pair={record?.cardTipsPaidInCash} fw={700} />}
          hint="Already out of the drawer, so the drop is smaller by exactly this"
        />
      </SimpleGrid>

      {/*
        The light night. The drawer could not even put its own float back, so the drop is nothing and
        the float box owes it the difference before anybody opens tomorrow. Silence here would leave
        the owner reading a drop of nought as a quiet evening rather than as something to do.

        Only once the drawer is counted off. "The next shift starts short" is a statement about a
        shift that has ended; a drawer three hours into a Saturday is routinely below its float with
        change out on the bar and paid-outs gone to the produce boat, and saying this then would put
        an orange panel on every busy evening for something that is not yet true of anything.
      */}
      {!trading && isSomething(record?.floatShortBy) && (
        <Alert color="orange" mt="md" title="The float box has to make this up">
          This drawer is below the float it opens with by{' '}
          <strong>
            {formatMoney(record?.floatShortBy.usdMinor, 'USD')} ·{' '}
            {formatMoney(record?.floatShortBy.hnlMinor, 'HNL')}
          </strong>
          . There is nothing to drop, and the next shift starts short unless the float box tops it back up.
        </Alert>
      )}

      {record && record.tips.length > 0 && (
        <>
          <Text size="sm" fw={600} mt="lg">
            Tips left on cards
          </Text>
          <Text size="sm" c="dimmed" mt={2} mb="sm">
            A tip on a card is money the bar owes a person and will not have in the bank for days, so it comes
            out of the drawer. On a light cash night there may not be enough — what is left stays owed and
            carries, and it is a debt rather than a shortage.
          </Text>
          <DataTable
            withTableBorder
            minHeight={80}
            records={record.tips}
            idAccessor={(row) => `${row.staffName ?? ''}|${row.currency}`}
            noRecordsText="No card tips on this shift."
            columns={[
              {
                accessor: 'staffName',
                title: 'Who',
                render: (row) => row.staffName ?? 'Nobody named',
              },
              {
                accessor: 'earnedOnCardsMinor',
                title: 'Earned on cards',
                render: (row) => formatMoney(row.earnedOnCardsMinor, row.currency),
              },
              {
                accessor: 'paidFromThisDrawerMinor',
                title: 'Handed over from this drawer',
                render: (row) => formatMoney(row.paidFromThisDrawerMinor, row.currency),
              },
              {
                accessor: 'stillOwedMinor',
                title: 'Still owed',
                render: (row) =>
                  row.stillOwedMinor === 0 ? (
                    <Text size="sm" c="dimmed">
                      Settled
                    </Text>
                  ) : (
                    <Text size="sm" c="orange" fw={600}>
                      {formatMoney(row.stillOwedMinor, row.currency)}
                    </Text>
                  ),
              },
            ]}
          />
          <Text size="xs" c="dimmed" mt="xs">
            Still owed counts every night, not just this one — a tip a light Tuesday could not cover is paid
            out of a better Friday.
          </Text>
        </>
      )}

      {drop.isError && (
        <Stack mt="md">
          <Text size="sm" c="red">
            The drop for this drawer could not be read.
          </Text>
        </Stack>
      )}
    </Paper>
  );
}
